import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CreditCard, CheckCircle2, Clock3, XCircle, Search, Eye, X, ChevronLeft, ChevronRight, ReceiptText, Smartphone, Wallet, RefreshCw, Loader2, AlertCircle, Printer } from "lucide-react";
import { getPaymentTransactions } from "../../api/paymentApi";
import SubscriptionInvoice from "../../components/members/SubscriptionInvoice";

const formatPrice = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const formatDate = (date) => {
  if (!date) return "-";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const normalizeStatus = (status) => {
  const value = String(status || "pending").toLowerCase();
  if (["success", "paid", "completed"].includes(value)) return "Success";
  if (["failed", "rejected"].includes(value)) return "Failed";
  if (value === "refunded") return "Refunded";
  return "Pending";
};

const normalizePayment = (item) => {
  const paymentDate = item.payment_date || item.paid_at || item.created_at || item.payment_created_at || null;
  const date = paymentDate ? new Date(paymentDate) : null;
  const paymentMode = String(item.payment_mode || item.method || "").toLowerCase();
  const modeLabel = {
    upi: "UPI",
    cash: "Cash",
    card: "Card",
    netbanking: "Net Banking",
    "net banking": "Net Banking",
    wallet: "Wallet",
    online: "Online",
  };
  return {
    id: item.payment_id || item.payment_rfId || item.rfId,
    subscriptionId: item.rfId || item.subscription_id,
    transactionId: item.transaction_reference || item.gateway_payment_id || item.payment_id || `PAY-${item.rfId}`,
    memberId: item.member_id || "-",
    memberName: item.member_name || item.full_name || item.name || "-",
    memberMobile: item.mobile_number || item.mobile || item.phone_number || "-",
    plan: item.plan_name_en || item.membership_plan_name || item.plan_name || "Membership Plan",
    amount: Number(item.paid_amount ?? item.total_amount ?? item.plan_amount ?? 0),
    planAmount: Number(item.plan_amount || 0),
    discountAmount: Number(item.discount_amount || 0),
    gstPercentage: Number(item.gst_percentage || 0),
    gstAmount: Number(item.gst_amount || 0),
    totalAmount: Number(item.total_amount || item.paid_amount || 0),
    taxableAmount: Number(item.taxable_amount ?? (Number(item.plan_amount || 0) - Number(item.discount_amount || 0))),
    method: modeLabel[paymentMode] || paymentMode || "-",
    rawMethod: paymentMode,
    status: normalizeStatus(item.payment_status),
    subscriptionStatus: item.subscription_status || "pending",
    paymentDate,
    paymentTime: date && !Number.isNaN(date.getTime()) ? date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", }) : "-",
    gateway: item.payment_gateway || item.gateway || "-",
    gatewayReference: item.gateway_payment_id || item.transaction_reference || "-",
    invoiceNumber: item.invoice_number || "",
    raw: item,
  };
};

function StatusBadge({ status }) {
  const styles = {
    Success: "bg-emerald-50 text-emerald-700",
    Pending: "bg-amber-50 text-amber-700",
    Failed: "bg-rose-50 text-rose-700",
    Refunded: "bg-violet-50 text-violet-700",
  };
  const dots = {
    Success: "bg-emerald-500",
    Pending: "bg-amber-500",
    Failed: "bg-rose-500",
    Refunded: "bg-violet-500",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${styles[status] || "bg-slate-100 text-slate-600"}`}    >
      <span className={`h-1.5 w-1.5 rounded-full ${dots[status] || "bg-slate-400"}`} />
      {status}
    </span>
  );
}

function StatCard({ title, value, description, icon: Icon, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500">{title}</p>
          <h3 className="mt-2 text-xl font-bold text-slate-800 sm:text-2xl">{value}</h3>
          <p className="mt-2 text-[11px] text-slate-400">{description}</p>
        </div>
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${colors[color]}`}>
          <Icon size={21} />
        </div>
      </div>
    </div>
  );
}

function PaymentDetailsModal({ payment, onClose, onViewBill }) {
  if (!payment) return null;
  const details = [
    ["Transaction ID", payment.transactionId],
    ["Subscription ID", payment.subscriptionId],
    ["Payment ID", payment.id],
    ["Member ID", payment.memberId],
    ["Member Name", payment.memberName],
    ["Mobile Number", payment.memberMobile],
    ["Membership Plan", payment.plan],
    ["Payment Method", payment.method],
    ["Payment Gateway", payment.gateway],
    ["Gateway Reference", payment.gatewayReference],
    ["Payment Date", formatDate(payment.paymentDate)],
    ["Payment Time", payment.paymentTime],
    ["Subscription Status", payment.subscriptionStatus],
  ];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Payment Details</h2>
            <p className="mt-1 text-xs text-slate-500">Transaction information and payment status</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
            <X size={19} />
          </button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto p-5 sm:p-6">
          <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-slate-500">Payment Amount</p>
                <h3 className="mt-1 text-2xl font-bold text-slate-800">{formatPrice(payment.amount)}</h3>
              </div>
              <CreditCard size={27} className="text-blue-600" />
            </div>
            <div className="mt-3">
              <StatusBadge status={payment.status} />
            </div>
          </div>
          <div className="mt-5 divide-y divide-slate-100">
            {details.map(([label, value]) => (
              <div key={label} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <span className="text-xs text-slate-500">{label}</span>
                <span className="break-all text-xs font-semibold text-slate-700 sm:max-w-[60%] sm:text-right">
                  {value || "-"}
                </span>
              </div>
            ))}
          </div>
          {payment.status === "Success" && (
            <button type="button" onClick={() => onViewBill(payment)} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700">
              <Printer size={16} />
              View / Print GST Bill
            </button>
          )}
          <button type="button" onClick={onClose} className="mt-3 w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [invoicePayment, setInvoicePayment] = useState(null);
  const loadPayments = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      else setRefreshing(true);
      setError("");
      const response = await getPaymentTransactions();
      const result = response?.data?.data ?? response?.data ?? [];
      if (response?.data?.success === false) {
        throw new Error(
          response?.data?.message || "Unable to fetch payments."
        );
      }
      if (!Array.isArray(result)) {
        throw new Error("Unexpected payment API response.");
      }
      setPayments(result.map(normalizePayment));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to load payment transactions."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const stats = useMemo(() => {
    const successful = payments.filter((p) => p.status === "Success");
    const pending = payments.filter((p) => p.status === "Pending");
    const refunded = payments.filter((p) => p.status === "Refunded");
    return [
      {
        title: "Total Transactions",
        value: payments.length,
        description: "All payment records",
        icon: CreditCard,
        color: "blue",
      },
      {
        title: "Successful Payments",
        value: formatPrice(
          successful.reduce((sum, p) => sum + p.amount, 0)
        ),
        description: `${successful.length} successful transactions`,
        icon: CheckCircle2,
        color: "green",
      },
      {
        title: "Pending Payments",
        value: formatPrice(
          pending.reduce((sum, p) => sum + p.amount, 0)
        ),
        description: `${pending.length} awaiting confirmation`,
        icon: Clock3,
        color: "amber",
      },
      {
        title: "Refunded Amount",
        value: formatPrice(
          refunded.reduce((sum, p) => sum + p.amount, 0)
        ),
        description: `${refunded.length} refunded transactions`,
        icon: XCircle,
        color: "violet",
      },
    ];
  }, [payments]);

  const filteredPayments = useMemo(() => {
    const term = search.trim().toLowerCase();
    return payments.filter((payment) => {
      const matchesSearch = !term ||
        String(payment.transactionId).toLowerCase().includes(term) ||
        String(payment.memberId).toLowerCase().includes(term) ||
        String(payment.memberName).toLowerCase().includes(term);
      const paymentDate = payment.paymentDate ? new Date(payment.paymentDate).toISOString().slice(0, 10) : "";
      return (
        matchesSearch &&
        (!statusFilter || payment.status === statusFilter) &&
        (!methodFilter || payment.method === methodFilter) &&
        (!fromDate || (paymentDate && paymentDate >= fromDate)) &&
        (!toDate || (paymentDate && paymentDate <= toDate))
      );
    });
  }, [payments, search, statusFilter, methodFilter, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const visiblePayments = filteredPayments.slice(startIndex, startIndex + pageSize);
  const resetFilters = () => { setSearch(""); setStatusFilter(""); setMethodFilter(""); setFromDate(""); setToDate(""); setPage(1); };
  const openInvoice = (payment) => {
    if (payment.status !== "Success") return;
    setSelectedPayment(null);
    setInvoicePayment({
      ...payment.raw,
      rfId: payment.subscriptionId,
      payment_id: payment.id,
      invoice_number: payment.invoiceNumber,
      plan_name_en: payment.plan,
      plan_amount: payment.planAmount,
      discount_amount: payment.discountAmount,
      taxable_amount: payment.taxableAmount,
      gst_percentage: payment.gstPercentage,
      gst_amount: payment.gstAmount,
      total_amount: payment.totalAmount,
      paid_amount: payment.amount,
      payment_status: payment.status,
      payment_mode: payment.rawMethod,
      payment_date: payment.paymentDate,
      transaction_reference: payment.gatewayReference,
      subscription_status: payment.subscriptionStatus,
      duration_days: payment.raw?.duration_days,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-400">
          <span>Home</span>
          <span>/</span>
          <span>Payments</span>
          <span>/</span>
          <span className="font-medium text-blue-600">Payment History</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-800 sm:text-2xl">Payment Management</h1>
            <p className="mt-1 text-xs text-slate-500 sm:text-sm">Track membership payments and review transaction details.</p>
          </div>
          <button
            type="button"
            onClick={() => loadPayments(false)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50">
            {refreshing ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <RefreshCw size={15} />
            )}
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Payment loading failed</p>
            <p className="mt-1">{error}</p>
            <button type="button" onClick={() => loadPayments()} className="mt-2 font-semibold underline">
              Try again
            </button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((item) => (
          <StatCard key={item.title} {...item} />
        ))}
      </div>
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4">
          <h2 className="text-sm font-bold text-slate-800">Search & Filters</h2>
          <p className="mt-1 text-xs text-slate-400">Search using transaction ID, member name or member ID.</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Transaction ID, name or member ID"
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-xs outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400">
            <option value="">All Payment Statuses</option>
            <option value="Success">Success</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
            <option value="Refunded">Refunded</option>
          </select>
          <select
            value={methodFilter}
            onChange={(e) => { setMethodFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400">
            <option value="">All Payment Methods</option>
            <option value="UPI">UPI</option>
            <option value="Card">Card</option>
            <option value="Net Banking">Net Banking</option>
            <option value="Wallet">Wallet</option>
            <option value="Cash">Cash</option>
            <option value="Online">Online</option>
          </select>
          <div>
            <label className="mb-1.5 block text-[10px] font-medium text-slate-500">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400" />
          </div>
          <div>
            <label className="mb-1.5 block text-[10px] font-medium text-slate-500">To Date</label>
            <input
              type="date"
              min={fromDate || undefined}
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(1); }}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-500">Found{" "}<strong className="text-slate-800">{filteredPayments.length}</strong>{" "}transaction(s)</p>
          <button
            type="button"
            onClick={resetFilters}
            className="rounded-lg border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            Reset Filters
          </button>
        </div>
      </section>
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h2 className="text-sm font-bold text-slate-800">Payment Transactions</h2>
            <p className="mt-1 text-[11px] text-slate-400">Membership payment history</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">Show
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5">
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            entries
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left">
            <thead className="bg-slate-50">
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Transaction</th>
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Membership Plan</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Payment Date</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-5 py-16 text-center">
                    <Loader2 size={28} className="mx-auto animate-spin text-blue-600" />
                    <p className="mt-3 text-sm text-slate-500">Loading payment transactions...</p>
                  </td>
                </tr>
              ) : visiblePayments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-14 text-center">
                    <ReceiptText size={28} className="mx-auto text-slate-300" />
                    <p className="mt-3 text-sm font-semibold text-slate-700">No payments found</p>
                    <p className="mt-1 text-xs text-slate-400">No payment transactions match your search or filters.</p>
                  </td>
                </tr>
              ) : (
                visiblePayments.map((payment) => (
                  <tr key={`${payment.id}-${payment.subscriptionId}`} className="transition hover:bg-slate-50/70">
                    <td className="whitespace-nowrap px-5 py-4">
                      <p className="text-xs font-semibold text-blue-600">{payment.transactionId}</p>
                      <p className="mt-1 text-[10px] text-slate-400">Subscription #{payment.subscriptionId}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <p className="text-xs font-semibold text-slate-800">{payment.memberName}</p>
                      <p className="mt-1 text-[10px] text-slate-400">{payment.memberId}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <span className="rounded-md bg-violet-50 px-2.5 py-1 text-[10px] font-semibold text-violet-700">{payment.plan}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-xs font-bold text-slate-800">
                      {formatPrice(payment.amount)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        {payment.method === "UPI" ? (
                          <Smartphone size={15} className="text-slate-400" />
                        ) : payment.method === "Card" ? (
                          <CreditCard size={15} className="text-slate-400" />
                        ) : (
                          <Wallet size={15} className="text-slate-400" />
                        )}
                        {payment.method}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <StatusBadge status={payment.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <p className="text-xs text-slate-600">{formatDate(payment.paymentDate)}</p>
                      <p className="mt-1 text-[10px] text-slate-400">{payment.paymentTime}</p>
                    </td>
                    <td className="whitespace-nowrap px-5 py-4">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          title="View payment details"
                          onClick={() => setSelectedPayment(payment)}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50">
                          <Eye size={16} />
                        </button>
                        {payment.status === "Success" && (
                          <button
                            type="button"
                            title="Print GST bill"
                            onClick={() => openInvoice(payment)}
                            className="rounded-lg p-2 text-emerald-600 hover:bg-emerald-50">
                            <Printer size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <p className="text-[11px] text-slate-500">
            Showing {filteredPayments.length ? startIndex + 1 : 0} to{" "}
            {Math.min(startIndex + pageSize, filteredPayments.length)} of{" "}
            {filteredPayments.length} entries
          </p>
          <div className="flex items-center gap-1 self-end sm:self-auto">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40">
              <ChevronLeft size={15} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).filter(
              (number) =>
                number === 1 ||
                number === totalPages ||
                Math.abs(number - safePage) <= 1
            ).map((number, index, numbers) => (
              <React.Fragment key={number}>
                {index > 0 && number - numbers[index - 1] > 1 && (
                  <span className="px-1 text-xs text-slate-400">...</span>
                )}
                <button
                  type="button"
                  onClick={() => setPage(number)}
                  className={`h-8 min-w-8 rounded-lg px-2 text-xs font-medium ${safePage === number
                    ? "bg-blue-600 text-white"
                    : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}>
                  {number}
                </button>
              </React.Fragment>
            ))}
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40">
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </section>
      {selectedPayment && (
        <PaymentDetailsModal payment={selectedPayment} onClose={() => setSelectedPayment(null)} onViewBill={openInvoice} />
      )}
      {invoicePayment && (
        <SubscriptionInvoice
          subscription={invoicePayment}
          member={{
            name: invoicePayment.member_name || invoicePayment.full_name,
            mobile_number:
              invoicePayment.mobile_number || invoicePayment.mobile,
            email: invoicePayment.email,
            address: invoicePayment.address,
            gstin: invoicePayment.customer_gstin,
          }}
          onClose={() => setInvoicePayment(null)} />
      )}
    </div>
  );
}